import type { ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, useCallback, useContext, useMemo } from 'react'
import { toast } from 'sonner'
import {
  FieldForm,
  type FieldFormProps,
} from '#src/components/core/form/FieldForm.tsx'
import {
  ResourceFieldForm,
  type ResourceFieldFormProps,
} from '#src/components/core/form/ResourceFieldForm.tsx'
import {
  NodeRecordContext,
  useNodeRecord,
} from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

// ResourceFieldForm for an observation field of an observation node's answer:
// a record is patched as ever; a draft held in the note has no record to
// patch, so the node changes it in place.
export function ObservationNodeFieldForm<TValue>(
  props: ResourceFieldFormProps<ExtendedObservation | undefined, TValue>
): JSX.Element {
  const node = useContext(NodeRecordContext)
  if (node?.isDraft && props.instance && !props.instance.toFhirResource().id) {
    return <DraftFieldForm {...props} />
  }
  return <ResourceFieldForm {...props} />
}

function DraftFieldForm<TValue>({
  instance,
  valueFn,
  patchFn,
  render,
  disabled,
  // A draft has no version history to keep short, so typing is held as it goes.
  debounceTyping: _debounceTyping,
  mutationOptions: _mutationOptions,
  skipVersionCheck: _skipVersionCheck,
  ...delegated
}: ResourceFieldFormProps<
  ExtendedObservation | undefined,
  TValue
>): JSX.Element {
  const { patch } = useNodeRecord()
  const isReadOnly = useIsReadOnly()
  const patchRef = useCurrentRef(patch)
  const patchFnRef = useCurrentRef(patchFn)

  const defaultValue = useMemo(() => {
    const value = instance ? valueFn(instance) : undefined
    return value ?? null
  }, [instance, valueFn])

  const handleSubmit = useCallback(
    (value: Parameters<typeof patchFn>[1]) => {
      patchRef
        .current((observation) => patchFnRef.current(observation, value))
        .catch((cause: unknown) => {
          toast.error(
            `Failed to change the answer${cause instanceof Error ? `: ${cause.message}` : ''}`
          )
        })
    },
    [patchRef, patchFnRef]
  )

  return (
    <FieldForm
      defaultValue={defaultValue}
      onSubmit={handleSubmit as FieldFormProps<TValue>['onSubmit']}
      disabled={!instance || disabled || isReadOnly}
      render={render as FieldFormProps<TValue>['render']}
      {...delegated}
    />
  )
}
